import {Link, Stack, TooltipHost, useTheme, Text} from '@fluentui/react';
import {useUserContext} from 'authentication/UserContext';
import {ArrayValues, StatusField} from 'components/EntityList/ColumnRenderers';
import i18n from 'i18n/i18n';
import {DateTimeFormat, formatDateTime} from 'i18n/localeDateFormat';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {getListClassNames} from 'styles';
import {KeywordStatus, VexWorkbenchItem} from 'types';
import {v4 as uuidv4} from 'uuid';

export const useVexWorkbenchItemsRenderer = (
	keywordStatus: KeywordStatus,
	proportionTable: number[],
	halfHeight: boolean,
) => {
	const {myVexClusters} = useUserContext();
	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);

	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'VexWorkbenchItems',
	});
	const getItemDueDate = useCallback(
		(item: VexWorkbenchItem) => {
			if (keywordStatus === KeywordStatus.Accepted) {
				return item.regulatoryDocument.workflow.dueDateInProcess;
			}
			if (keywordStatus === KeywordStatus.New) {
				return item.regulatoryDocument.workflow.dueDate;
			}
			return undefined;
		},
		[keywordStatus],
	);
	const isOverDueDate = useCallback(
		(item: VexWorkbenchItem) => {
			const dueDate = getItemDueDate(item);
			return new Date() > new Date(dueDate);
		},
		[getItemDueDate],
	);

	const getDueDate = useCallback(
		(item: VexWorkbenchItem) => {
			const dueDate = getItemDueDate(item);
			return dueDate
				? formatDateTime(
						new Date(dueDate),
						i18n,
						DateTimeFormat.DateMonthWeekday,
				  )
				: '';
		},
		[getItemDueDate, i18n],
	);

	const onRenderVexItemCell = (
		keywordStatus: KeywordStatus,
		item?: VexWorkbenchItem,
		columnKey?: string,
	): JSX.Element => {
		if (!item) {
			return <></>;
		}

		const itemClasses = isOverDueDate(item)
			? `${classNames.itemCellPadding0} ${classNames.itemCellError}`
			: classNames.itemCellPadding0;

		const getSimpleField = (content: any, index: number) => {
			return (
				<div
					style={{
						...commonFieldStyle,
						width: `${proportionTable[index]}%`,
					}}
				>
					{content}
				</div>
			);
		};

		const shrinkNames = (names: string[]) => {
			return names.map(name => {
				if (name.length > 16) {
					return (
						<TooltipHost content={name} key={uuidv4()}>
							<div>{`${name.substring(0, 13)}...`}</div>
						</TooltipHost>
					);
				}
				return <div key={uuidv4()}>{`${name}`}</div>;
			});
		};

		const markets =
			item.regulatoryDocument?.regulation?.markets?.map(m => m.name) || [];

		const vexClusters = item.regulatoryDocument?.paragraphs?.flatMap(par =>
			par.keywordAssignments?.flatMap(ka =>
				ka.vexClusterAssignments?.flatMap(vca => vca?.vexCluster),
			),
		);
		let vexClusterNames =
			vexClusters
				.filter(vc => vc && myVexClusters.some(myVc => myVc.id === vc.id))
				.map(vc => vc?.name || '') || [];

		vexClusterNames = vexClusterNames.filter(
			(vc: any, index: number) => vexClusterNames.indexOf(vc) === index,
		);

		const status = item.regulatoryDocument?.status || '';
		const statusField = status ? (
			<StatusField
				item={item}
				enumName='RegulatoryDocumentStatus'
				value={status}
			/>
		) : (
			<></>
		);

		const paragraphCountArr =
			item.regulatoryDocument?.paragraphs?.reduce((acc, par) => {
				const vexClustersFoundInThisParagraph = [] as string[];
				for (let i = 0; i < (par?.keywordAssignments?.length || 0); i++) {
					if (!par.keywordAssignments) {
						continue;
					}
					const ka = par.keywordAssignments[i];
					for (let j = 0; j < ka.vexClusterAssignments?.length; j++) {
						const vca = ka.vexClusterAssignments[j];
						if (
							!vexClustersFoundInThisParagraph.includes(vca.vexCluster.id) &&
							vca.keywordStatus === keywordStatus
						) {
							const name = vca.vexCluster?.name;
							acc[name] = acc[name] ? acc[name] + 1 : 1;
							vexClustersFoundInThisParagraph.push(vca.vexCluster.id);
						}
					}
				}
				return acc;
			}, {} as Record<string, number>) || [];

		const paragraphCountSortedByVexClusters = vexClusterNames.map(vcName => {
			return `${paragraphCountArr[vcName] || '0'}`;
		});

		const commonFieldStyle = {textAlign: 'center' as const};

		return (
			<div
				key={item.regulatoryDocument.id}
				className={itemClasses}
				data-is-focusable={true}
				style={{textDecoration: 'none'}}
			>
				<Stack
					horizontal
					horizontalAlign={'space-between'}
					styles={{root: {width: '100%', padding: '8px 0'}}}
				>
					<div style={{width: `${proportionTable[0]}%`, padding: '0 8px'}}>
						<a
							href={`/regulations/${item?.regulatoryDocument?.regulation?.id}/${
								item?.regulatoryDocument?.id
							}/paragraphs?filter=${
								keywordStatus === KeywordStatus.Accepted ? 'inProcess' : 'new'
							}`}
						>
							<Text>
								{item.regulatoryDocument.regulation?.regulationNumber ?? ''}
							</Text>
							<div style={{color: theme.palette.neutralDark}}>
								{item?.regulatoryDocument?.name} - {statusField}
							</div>
							{columnKey !== t('CompletedTitle') && (
								<div className={classNames.itemSubtext}>
									{`${t('DueDate')}:${getDueDate(item)}`}
								</div>
							)}
						</a>
					</div>
					{getSimpleField(
						<ArrayValues array={shrinkNames(markets)} limit={4} />,
						1,
					)}
					{getSimpleField(
						<ArrayValues array={shrinkNames(vexClusterNames)} limit={4} />,
						2,
					)}
					{columnKey !== t('CompletedTitle') && (
						<div style={{textAlign: 'right', width: `${proportionTable[3]}%`}}>
							<ArrayValues
								array={paragraphCountSortedByVexClusters}
								limit={4}
								forceColumnFlow={true}
							/>
						</div>
					)}
				</Stack>
			</div>
		);
	};

	return {
		onRenderVexItemCell,
	};
};
